import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { logError } from '../services/app-logger';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setSyncFunction: (syncFn: (theme: Theme) => Promise<void>) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: Theme;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [syncFunction, setSyncFunction] = useState<((theme: Theme) => Promise<void>) | null>(null);
  const [theme, setThemeState] = useState<Theme>(() => {
    // Priority order:
    // 1. Use initial theme from backend if provided (user's saved preference)
    if (initialTheme) {
      localStorage.setItem('theme', initialTheme);
      return initialTheme;
    }
    
    // 2. Check local storage (for cases where backend settings not yet loaded)
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) return savedTheme;
    
    // 3. Default to system
    return 'system';
  });

  // Calculate effective theme based on theme setting
  const effectiveTheme = React.useMemo((): 'light' | 'dark' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme as 'light' | 'dark';
  }, [theme]);

  // Update theme when backend settings change
  useEffect(() => {
    if (initialTheme && initialTheme !== theme) {
      setThemeState(initialTheme);
      localStorage.setItem('theme', initialTheme);
    }
  }, [initialTheme]);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(effectiveTheme);
  }, [effectiveTheme]);

  // Handle system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(mediaQuery.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Sync with backend if sync function is available
    if (syncFunction) {
      syncFunction(newTheme).catch(error => {
        logError('Failed to sync theme with backend:', error);
      });
    }
  }, [syncFunction]);

  const toggleTheme = useCallback(() => {
    const newTheme = effectiveTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [effectiveTheme, setTheme]);

  const setSyncFunctionCallback = useCallback((syncFn: (theme: Theme) => Promise<void>) => {
    setSyncFunction(() => syncFn);
  }, []);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      effectiveTheme, 
      toggleTheme, 
      setTheme,
      setSyncFunction: setSyncFunctionCallback
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
